/**
 * @description 活动是否在有效期内（商详 / 购物车共用）
 */

import {
  isComboDetailActivityInProgress,
  isComboDetailPromotionStatusUnavailable,
  isStorePromotionActivityInProgress,
  isStorePromotionLineStatusAvailable,
  isStorePromotionLineStatusUnavailable,
  isStorePromotionStatusUnavailableFromSources,
} from '~/utils/product'
import { isComboPackage, isComboBundle, PROMOTION_TYPE } from './promotionTypes'

/**
 * @description 订单确认/详情等是否展示行内活动信息（`promotionStatus` 为 true/1 且为活动行）
 * @param {Object|null|undefined} record
 * @returns {boolean}
 */
export const shouldShowOrderPromotionUi = (record) => {
  const pt = Number(record?.promotionType) || 0
  if (pt === 0) return false
  return isStorePromotionLineStatusAvailable(record)
}

/**
 * @description 活动状态不可用（`promotionStatus` 为 false/0）时剥离订单确认/详情/售后行上的活动展示字段
 * @param {Object|null|undefined} record
 * @param {{ preserveGiveawayList?: boolean }} [options] - 订单详情保留已落单赠品列表
 * @returns {Object|null|undefined}
 */
export const stripOrderLineInactivePromotionDisplay = (record, options = {}) => {
  if (!record || typeof record !== 'object') return record
  if (shouldShowOrderPromotionUi(record)) return record
  const preserveGiveawayList = options.preserveGiveawayList === true
  return {
    ...record,
    markingDiscountTag: '',
    linkedAppliedMarkingHint: '',
    promotionTag: '',
    giveawayList: preserveGiveawayList
      ? Array.isArray(record.giveawayList)
        ? record.giveawayList
        : []
      : [],
    gifts: preserveGiveawayList ? (Array.isArray(record.gifts) ? record.gifts : []) : [],
    giftNames: preserveGiveawayList
      ? Array.isArray(record.giftNames)
        ? record.giftNames
        : []
      : [],
  }
}

/**
 * @description 购物车扁平行活动是否进行中（优先读 adapter 写入的 `promotionActivityInProgress`）
 * @param {Object|null|undefined} record
 * @returns {boolean}
 */
export const isCartLinePromotionActivityInProgress = (record) => {
  if (!record || typeof record !== 'object') return false
  if (record.promotionActivityInProgress === true) return true
  if (record.promotionActivityInProgress === false) return false

  const pt = Number(record.promotionType) || 0
  // 套餐类活动(4/5)检查comboVO/promotionVO
  if (isComboPackage(pt) && record.comboVO) {
    return isComboDetailActivityInProgress(record.comboVO)
  }
  if (isComboPackage(pt) && record.promotionVO) {
    return isComboDetailActivityInProgress(record.promotionVO)
  }

  return isStorePromotionActivityInProgress({
    promotionType: record.promotionType,
    promotionId: record.promotionId,
    promotionStatus: record.promotionStatus,
    seckillVO: record.seckillVO,
    discountVO: record.discountVO,
    giveawayVO: record.giveawayVO,
    comboVO: record.comboVO,
  })
}

/**
 * @description 解析购物车行活动是否进行中（V3 原始行，供 adapter 使用）
 * @param {Object} line - shopCartItem
 * @param {Object|null|undefined} comboVO - 套餐主行 comboVO（子行可传入 leader 的 VO）
 * @returns {boolean}
 */
export const resolveCartV3LinePromotionActivityInProgress = (line, comboVO) => {
  if (!line || typeof line !== 'object') return false
  const pt = Number(line.promotionType) || 0
  if (pt === 0) return false

  // 优先使用后端返回的 promotionActivityInProgress 字段
  if (line.promotionActivityInProgress === true) return true
  if (line.promotionActivityInProgress === false) return false

  // 套餐类活动(4/5)
  if (isComboPackage(pt)) {
    const vo = line.comboVO ?? line.promotionVO ?? comboVO
    return vo ? isComboDetailActivityInProgress(vo) : false
  }

  return isStorePromotionActivityInProgress({
    promotionType: line.promotionType,
    promotionId: line.promotionId,
    promotionStatus: line.promotionStatus,
    seckillVO: line.seckillVO,
    discountVO: line.discountVO,
    giveawayVO: line.giveawayVO,
  })
}

/**
 * @description 购物车套餐主商品行（`promotionType=3` 且带 comboVO / 组 leader）
 * @param {Object|null|undefined} record
 * @returns {boolean}
 */
export const isCartComboPackageMainLine = (record) => {
  if (!record || typeof record !== 'object') return false
  if (!isComboPackage(record.promotionType)) return false
  // 搭配套餐(4)：有主商品概念
  if (isComboBundle(record.promotionType)) {
    if (record.isComboPackageMain === true) return true
    if (Number(record.comboItemType) === 1) return true
  }
  // 组合促销(5)：无主商品，第一个是leader
  const leaderId = record.comboGroupLeaderBasketId
  if (leaderId == null || leaderId === '') return false
  const rowId = record.basketId ?? record.id
  return Number(rowId) === Number(leaderId)
}

/**
 * @description 购物车行活动「状态」是否不可用（与活动时间 `promotionActivityInProgress` 分开判断）
 * @param {Object|null|undefined} record
 * @returns {boolean}
 */
export const isCartLinePromotionStatusUnavailable = (record) => {
  if (!record || typeof record !== 'object') return false
  const extra = [record.seckillVO, record.discountVO, record.giveawayVO, record.comboVO].filter(
    (src) => src && typeof src === 'object'
  )
  return isStorePromotionStatusUnavailableFromSources(record, extra)
}

/**
 * @description 购物车行是否按活动价/规则参与活动（有效期内且活动状态可用）
 * @param {Object|null|undefined} record
 * @returns {boolean}
 */
export const isCartLinePromotionParticipating = (record) => {
  const pt = Number(record?.promotionType) || 0
  if (pt === 0) return false
  if (!isCartLinePromotionActivityInProgress(record)) return false
  if (!isStorePromotionLineStatusAvailable(record)) return false
  if (isCartLinePromotionStatusUnavailable(record)) return false
  return true
}

/**
 * @description 非套餐活动行是否已失效（不参与活动：过期或状态不可用；套餐走 `isCartComboPackageActivityInvalid`）
 * @param {Object|null|undefined} record
 * @returns {boolean}
 */
export const isCartNonComboPromotionExpired = (record) => {
  const pt = Number(record?.promotionType) || 0
  if (pt === 0 || isComboPackage(pt)) return false
  return !isCartLinePromotionParticipating(record)
}

/**
 * @description 套餐活动状态是否不可用（行 `promotionStatus` 或 `comboVO.status` 非启用）
 * @param {Object|null|undefined} record
 * @returns {boolean}
 */
export const isCartComboPromotionStatusUnavailable = (record) => {
  if (!record || typeof record !== 'object') return false
  if (isStorePromotionLineStatusUnavailable(record)) return true
  return isComboDetailPromotionStatusUnavailable(record.comboVO)
}

/**
 * @description 套餐实例是否已失效（非活动期或活动状态不可用）：整组禁止勾选/改数量，仅可删除
 * @param {Object|null|undefined} record
 * @returns {boolean}
 */
export const isCartComboPackageActivityInvalid = (record) => {
  if (!record || typeof record !== 'object') return false
  if (!isComboPackage(record.promotionType)) return false
  const gk = record.comboGroupKey
  if (gk == null || gk === '') return false
  if (!isCartLinePromotionActivityInProgress(record)) return true
  if (isCartComboPromotionStatusUnavailable(record)) return true
  return false
}

/**
 * @description 购物车套餐行是否在数量区展示「活动已失效」（仅 `promotionType=3` 且整组套餐活动失效）
 * @param {Object|null|undefined} record - 扁平行 / store 行
 * @returns {boolean}
 */
export const showCartLineActivityInvalidTag = (record) => {
  if (!record || typeof record !== 'object') return false
  if (!isComboPackage(record.promotionType)) return false
  const gk = record.comboGroupKey
  if (gk == null || gk === '') return false
  return isCartComboPackageActivityInvalid(record)
}

/**
 * @description 购物车套餐主商品图角标是否展示「活动已失效」（与 `showCartLineActivityInvalidTag` 套餐场景一致）
 * @param {Object|null|undefined} record
 * @returns {boolean}
 */
export const showCartComboActivityInvalidTag = (record) => {
  if (!isCartComboPackageMainLine(record)) return false
  return showCartLineActivityInvalidTag(record)
}
